import { ArrowBack as CancelIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, Grid, IconButton, TextField, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import type { FC } from 'react';
import { useState } from 'react';
import { useSetTitle } from '../../hooks/use-title.js';

export interface ForgotPasswordData {
  email: string;
}

interface ViewProps<T> {
  busy: boolean;
  cancel: () => void;
  data: T;
  patchData: <K extends keyof T, V extends T[K]>(key: K, value: V) => void;
  sendEmail: () => void;
}

const forgotPassword = (email: string): Promise<void> => {
  return axios.post('/api/v1/forgot-password', { email });
};

export const ForgotPassword: FC<{
  view?: FC<ViewProps<ForgotPasswordData>>;
  back: () => void;
}> = ({ view: View = ForgotPasswordView, back }) => {
  useSetTitle('Forgot Password');
  const { enqueueSnackbar } = useSnackbar();

  const [busy, setBusy] = useState(false);
  const [data, setData] = useState<ForgotPasswordData>({ email: '' });
  const patchData = <
    K extends keyof ForgotPasswordData,
    V extends ForgotPasswordData[K],
  >(
    key: K,
    value: V,
  ): void => {
    setData({
      ...data,
      [key]: value,
    });
  };

  return (
    <View
      busy={busy}
      cancel={back}
      data={data}
      patchData={patchData}
      sendEmail={() => {
        setBusy(true);
        forgotPassword(data.email)
          .then(() => {
            back();
            enqueueSnackbar('An email is on its way.', { variant: 'success' });
            return undefined;
          })
          .catch((err) => {
            console.log(err);
            Sentry.captureException(err);
            enqueueSnackbar(String(err), { variant: 'error' });
            setBusy(false);
          });
      }}
    />
  );
  // TODO display success message
};

export const ForgotPasswordView: FC<ViewProps<ForgotPasswordData>> = ({
  busy,
  cancel,
  data,
  patchData,
  sendEmail,
}) => (
  <form
    onSubmit={(event) => {
      event.preventDefault();
      sendEmail();
    }}
  >
    <Box
      sx={{
        mx: 'auto',
        width: 200,
      }}
    >
      <Typography sx={{ mb: 2, textAlign: 'center' }} variant="h4">
        Forgot Password?
      </Typography>
    </Box>
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography>
          Submit the email address associated with your account below. We'll
          send instructions to reset you password to you.
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          id="email"
          label="Email address"
          name="email"
          onChange={(event) => patchData('email', event.target.value)}
          required
          type="email"
          value={data.email}
        />
      </Grid>
      <Grid
        item
        sx={{ display: 'flex', justifyContent: 'space-between' }}
        xs={12}
      >
        <IconButton color="primary" onClick={cancel}>
          <CancelIcon />
        </IconButton>
        <LoadingButton
          loading={busy}
          size="large"
          sx={{
            borderRadius: 100,
          }}
          type="submit"
          variant="contained"
        >
          Send email
        </LoadingButton>
      </Grid>
    </Grid>
  </form>
);
